/**
 * A company's short name, which is now its address —
 * `docs/identity-and-sign-in.md` §5.5.
 *
 * It used to be a path segment: `app.rowted.com/keystone/jobs`. A path can be
 * almost anything, so the rule was loose and lived in two places that happened
 * to agree. A **host label** cannot: it is what a browser scopes a cookie to,
 * what a certificate covers, and what DNS resolves, so the rule has to be one
 * rule and every layer has to hold it — the flyout that offers a name, the
 * server that claims it, the middleware that reads it back out of a `Host`
 * header, and the CHECK in migration 0091 that stops a company existing at an
 * address nobody can reach.
 *
 * This file is that one rule. The migration repeats the pattern in SQL, which
 * is the one duplication that cannot be avoided; its comment names this file.
 */
/**
 * Three to thirty characters: lower-case letters, digits and dashes, never
 * starting or ending with a dash.
 *
 * §5.5 wrote it as `^[a-z0-9](?:[a-z0-9-]{1,28}[a-z0-9])?$`, whose optional
 * group makes the whole thing admit **one** character and then jump to three.
 * The one-character branch is the mistake: board 5 says "Letters, numbers and
 * hyphens · 3–30" in the field's own hint, so three is the rule and a
 * single-letter subdomain was never intended. Thirty stays — well inside a DNS
 * label's 63, and this is a *short* name.
 */
export const SLUG_PATTERN = /^[a-z0-9][a-z0-9-]{1,28}[a-z0-9]$/;
export const SLUG_MIN = 3;
export const SLUG_MAX = 30;
/**
 * Labels that are Rowted's own. A company can never hold one, because
 * `admin.app.rowted.com` is the console and `api.app.rowted.com` is not a
 * company's sign-in page.
 *
 * Board 5's field lists eight — app, admin, api, www, portal, crew, mail,
 * status. The four below it (docs, static, assets, cdn, pay) are here too: the
 * list can only ever grow, and a name taken by a customer before it is
 * reserved is a name that cannot be reserved afterwards without breaking
 * them.
 *
 * The list only ever grows, and a name added to it is a name that has to be
 * free: migration 0091 flags any tenant already holding one rather than
 * renaming it, because there is nothing to rename it *to* that would not break
 * the links already sent.
 */
export const RESERVED_SLUGS = [
    'app', 'admin', 'api', 'www', 'portal', 'crew', 'mail', 'status', 'help',
    'docs', 'static', 'assets', 'cdn', 'pay',
    /*
     * Rowted's own mail (Aaron, Oct 27, answer 17): a company's email goes out
     * from `<slug>@mail.rowted.com`, and Rowted's own from
     * `rowted@mail.rowted.com` — so a company holding `rowted` would share
     * Rowted's address, and its customers' replies would read as Rowted's.
     * Migration 0153 flags a company already holding it.
     */
    'rowted',
];
/**
 * The local part Rowted's own mail goes out from on the mail domain —
 * `rowted@mail.rowted.com` (`services/postmark.ts`). Reserved above, so no
 * company can ever be it, and a reply to it is never filed on a company's
 * conversation.
 */
export const PLATFORM_MAIL_LOCAL_PART = 'rowted';
export const isReservedSlug = (slug) => RESERVED_SLUGS.includes(slug.trim().toLowerCase());
/**
 * What is wrong with this short name, in the words the field puts under
 * itself — or null when nothing is.
 *
 * One function rather than a boolean, so the flyout, the API and a future
 * rename all say the same sentence. A person who reads "That is Rowted's" on
 * the screen and "invalid slug" in a 400 has been told two different things
 * about one mistake.
 */
export function slugProblem(asked) {
    const slug = asked.trim().toLowerCase();
    if (!slug)
        return 'A short name is needed — it is the company’s web address.';
    if (slug.length < SLUG_MIN)
        return 'Too short — at least three characters.';
    if (slug.length > SLUG_MAX)
        return `Too long — at most ${SLUG_MAX} characters.`;
    if (isReservedSlug(slug))
        return 'That one is Rowted’s own. Try another.';
    if (!SLUG_PATTERN.test(slug)) {
        return 'Letters, numbers and dashes only, and not starting or ending with a dash.';
    }
    return null;
}
/**
 * A company name turned into something that could be an address.
 *
 * Shared so that the flyout's live suggestion and the server's fallback cannot
 * drift: what the field showed while somebody typed is what they get if they
 * never touch it.
 */
export function slugify(company) {
    const base = company.toLowerCase().normalize('NFKD')
        .replace(/[^a-z0-9\s-]/g, '')
        .trim()
        .replace(/[\s_]+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, SLUG_MAX)
        // A trailing dash can only appear here, from the slice.
        .replace(/-+$/, '');
    return base.length >= SLUG_MIN ? base : 'company';
}
//# sourceMappingURL=tenant-slug.js.map